import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { ArticleSchema } from '@/components/schema/ArticleSchema';
import { BreadcrumbSchema } from '@/components/schema/BreadcrumbSchema';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchPost();
      fetchRelatedPosts();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}`);
      setPost(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(err.response?.status === 404 ? 'Beitrag nicht gefunden' : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}/related`);
      setRelatedPosts(response.data);
    } catch (err) {
      console.error('Error fetching related posts:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link in Zwischenablage kopiert!');
    }
  };

  const breadcrumbItems = [
    { name: 'Home', url: `${window.location.origin}/` },
    { name: 'Blog', url: `${window.location.origin}/blog` },
    { name: post?.title || 'Beitrag', url: window.location.href }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Skeleton className="h-96 w-full mb-8" />
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-12 text-center">
              <div className="text-6xl mb-4">📄</div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                {error || 'Beitrag nicht gefunden'}
              </h2>
              <Link to="/blog">
                <Button className="bg-[#2563A0] text-white hover:bg-[#1B4D7A]">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück zum Blog
                </Button>
              </Link>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <ArticleSchema
        title={post.title}
        description={post.excerpt || post.meta_description}
        author={post.author}
        publishedDate={post.published_at}
        modifiedDate={post.updated_at}
        imageUrl={post.hero_image_url}
        url={window.location.href}
      />
      <BreadcrumbSchema items={breadcrumbItems} />
      <SEO
        title={`${post.title} | RetailInvest Blog`}
        description={post.meta_description || post.excerpt}
        url={`/blog/${post.slug}`}
        keywords={post.meta_keywords?.join(', ')}
        image={post.hero_image_url}
      />
      <Navbar />
      
      <main className="flex-grow bg-gray-50 py-12">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#2563A0] hover:text-[#1B4D7A] mb-8 font-medium"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zum Blog
          </Link>

          {/* Hero Image */}
          {post.hero_image_url && (
            <div className="relative h-96 rounded-2xl overflow-hidden mb-8">
              <img
                src={post.hero_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 to-transparent"></div>
            </div>
          )}

          {/* Header */}
          <header className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map(tag => (
                <Badge key={tag} className="bg-[#EFF6FF] text-[#2563A0]">
                  {tag}
                </Badge>
              ))}
            </div>

            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: 'var(--navy-900)' }}
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--gray-600)' }}>
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formatDate(post.published_at)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {Math.ceil(post.content_html.length / 1000)} Min Lesezeit
                </span>
                <span>Von {post.author}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                data-testid="share-button"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Teilen
              </Button>
            </div>
          </header>

          {/* Content */}
          <Card className="p-8 md:p-12 mb-12">
            <div
              className="blog-article-content"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
              data-testid="blog-post-content"
            />
          </Card>

          {/* CTA Section */}
          <Card className="p-8 bg-[#EFF6FF] mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
              Haben Sie Fragen zum Verkauf Ihrer Handelsimmobilie?
            </h3>
            <p className="text-base mb-6" style={{ color: 'var(--gray-700)' }}>
              Unser Team berät Sie gerne kostenlos und unverbindlich zu allen Aspekten des Verkaufs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/kontakt">
                <Button className="bg-[#2563A0] text-white hover:bg-[#1B4D7A]">
                  Jetzt Kontakt aufnehmen
                </Button>
              </Link>
              <Link to="/tools">
                <Button variant="outline">
                  Kostenlose Tools nutzen
                </Button>
              </Link>
            </div>
          </Card>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div data-testid="related-posts">
              <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                Ähnliche Beiträge
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group"
                  >
                    <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow">
                      {relatedPost.hero_image_url && (
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={relatedPost.hero_image_url}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-bold text-base mb-2 line-clamp-2 group-hover:text-[#D4A853] transition-colors" style={{ color: 'var(--navy-800)' }}>
                          {relatedPost.title}
                        </h4>
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--gray-600)' }}>
                          {relatedPost.excerpt}
                        </p>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
